<template>
  <div
    class="appointment-report ar-action-bar"
    :class="[
      `ar-action-bar--${variant}`,
      {
        'ar-action-bar--sticky': sticky && variant === 'page',
        'ar-anim-ctl-in': entering,
      },
    ]"
  >
    <div
      v-if="error || $slots.error"
      class="ar-action-bar__error-wrap"
    >
      <slot name="error">
        <div
          :id="errorId"
          role="alert"
          class="ar-callout ar-callout--danger ar-anim-fade-in ar-action-bar__error"
          :style="innerStyle"
        >
          <feather-icon
            icon="AlertTriangleIcon"
            size="20"
            aria-hidden="true"
          />
          <p class="ar-action-bar__error-text">
            {{ resolvedErrorMessage }}
          </p>
        </div>
      </slot>
    </div>

    <div class="ar-action-bar__bar">
      <div
        class="ar-action-bar__inner"
        :style="innerStyle"
      >
        <div class="ar-action-bar__status">
          <button
            type="button"
            class="ar-action-bar__status-btn"
            :class="{ 'ar-action-bar__status-btn--ready': ready }"
            @click="onStatusClick"
          >
            <transition
              name="ar-fade"
              mode="out-in"
            >
              <span
                :key="statusText"
                class="ar-action-bar__status-content"
              >
                <feather-icon
                  :icon="statusIcon"
                  size="16"
                  class="ar-action-bar__status-icon"
                  aria-hidden="true"
                />
                <span class="ar-action-bar__status-text">{{ statusText }}</span>
              </span>
            </transition>
          </button>
          <span
            v-if="savedLabel"
            :key="savedLabel + savedTick"
            class="ar-action-bar__saved ar-tabular"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="m17 15-5.5 5.5L9 18" />
              <path d="M5 17.743A7 7 0 1 1 15.71 10h1.79a4.5 4.5 0 0 1 1.5 8.742" />
            </svg>
            {{ savedLabel }}
          </span>
        </div>

        <div class="ar-action-bar__actions">
          <slot name="secondary" />
          <button
            :id="resolvedCtaId"
            ref="cta"
            type="button"
            class="ar-btn ar-action-bar__cta"
            :class="ctaClasses"
            :aria-disabled="canFinalize ? 'false' : 'true'"
            :aria-busy="busy ? 'true' : 'false'"
            :aria-describedby="error ? errorId : null"
            @click="onCtaClick"
          >
            <svg
              v-if="busy"
              class="ar-spinner"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                stroke-opacity=".25"
                stroke-width="2.5"
              />
              <path
                d="M21 12a9 9 0 0 0-9-9"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
              />
            </svg>
            <span>{{ ctaText }}</span>
            <kbd
              v-if="canFinalize && shortcut"
              class="ar-kbd ar-anim-fade-in"
              aria-hidden="true"
            >{{ NEW_COPY.shortcutHint }}</kbd>
          </button>
        </div>
      </div>
    </div>

    <span
      class="ar-sr-only"
      aria-live="polite"
    >{{ announcement }}</span>
  </div>
</template>

<script>
import { FIGMA_COPY, NEW_COPY } from '@/constants/appointment_report_v2'
import {
  formatShortDate,
  formatTime,
  isWallClockToday,
  nextReportUid,
} from '../helpers/report_ui'

const PULSE_MS = 760
const TIME_ONLY = /^\d{2}:\d{2}$/

/**
 * Barra de ação do Report (spec-v3 §2.G / v4): à esquerda o status ("Faltam N respostas" → "Tudo pronto para
 * finalizar") e a linha do autosave ("Rascunho salvo às 14:05"); à direita o CTA "Finalizar Momento X".
 *
 * - Incompleto: CTA "soft-disabled" (`aria-disabled="true"`, continua clicável) → emite `jump-to-missing`;
 *   o botão de status faz o mesmo.
 * - Completo: CTA cheio + dica "Ctrl ↵" (Ctrl/⌘+Enter também finaliza) + UM pulso suave (uma vez por montagem).
 * - `busy`: spinner + "Finalizando…", cliques ignorados. `error`: banner (role="alert") acima da barra e o CTA
 *   vira "Tentar novamente".
 * - `lockedText`: a finalização espera algo fora do formulário (ex.: o horário da reunião). O status mostra o texto
 *   (relógio), o CTA fica "soft-disabled" e o clique (ou o atalho) só anuncia o texto e emite `locked`.
 *
 * `variant="page"` fica fixa no rodapé da coluna (sticky); `variant="pane"` é o rodapé do painel da central.
 */
export default {
  name: 'ReportActionBar',
  props: {
    missingCount: {
      type: Number,
      required: true,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    // Horário do último autosave: ISO da API (`draft.updated_at`) ou "HH:MM". Vazio = sem linha.
    savedAt: {
      type: String,
      default: '',
    },
    ctaLabel: {
      type: String,
      default: '',
    },
    // Vazio = sem trava. Ver o comentário do componente.
    lockedText: {
      type: String,
      default: '',
    },
    variant: {
      type: String,
      default: 'page',
      validator: value => ['page', 'pane'].includes(value),
    },
    sticky: {
      type: Boolean,
      default: true,
    },
    // Ctrl/⌘+Enter finaliza (ou leva à pendência) enquanto o foco está no formulário desta barra.
    shortcut: {
      type: Boolean,
      default: true,
    },
    maxWidth: {
      type: String,
      default: '',
    },
    ctaId: {
      type: String,
      default: '',
    },
    // Formulário acabou de ser liberado no horário: a barra entra junto com os controles (fade + 4px).
    entering: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      NEW_COPY,
      uid: nextReportUid('ar-bar'),
      pulsing: false,
      pulsedOnce: false,
      announcement: '',
      savedTick: 0,
    }
  },
  computed: {
    locked() {
      return !!this.lockedText
    },
    ready() {
      return !this.locked && this.missingCount <= 0
    },
    canFinalize() {
      return this.ready && !this.busy
    },
    statusText() {
      if (this.locked) return this.lockedText
      return this.ready ? NEW_COPY.readyToFinalize : NEW_COPY.missingAnswers(this.missingCount)
    },
    statusIcon() {
      if (this.locked) return 'ClockIcon'
      return this.ready ? 'CheckCircleIcon' : 'AlertCircleIcon'
    },
    ctaText() {
      if (this.busy) return NEW_COPY.finalizing
      if (this.error) return NEW_COPY.retry
      return this.ctaLabel || FIGMA_COPY.finalizeButton
    },
    ctaClasses() {
      return {
        'ar-btn--primary': this.ready || this.busy,
        'ar-btn--soft': !this.ready && !this.busy,
        'ar-action-bar__cta--busy': this.busy,
        'ar-anim-pulse': this.pulsing,
      }
    },
    resolvedCtaId() {
      return this.ctaId || `${this.uid}-cta`
    },
    errorId() {
      return `${this.uid}-error`
    },
    resolvedErrorMessage() {
      return this.errorMessage || NEW_COPY.finalizeError
    },
    savedLabel() {
      if (!this.savedAt) return ''
      if (TIME_ONLY.test(this.savedAt)) return NEW_COPY.draftSavedAt(this.savedAt)
      const time = formatTime(this.savedAt)
      if (!time) return ''
      // Rascunho de outro dia (ex.: restaurado na central, onde toda pendência é de ontem ou antes): o horário
      // sozinho apontaria para hoje — "às 16:42 de 29/09/26", como o banner de horário.
      if (isWallClockToday(this.savedAt)) return NEW_COPY.draftSavedAt(time)

      return NEW_COPY.draftSavedAt(`${time} de ${formatShortDate(this.savedAt)}`)
    },
    innerStyle() {
      if (this.maxWidth) return { maxWidth: this.maxWidth }
      return this.variant === 'page' ? { maxWidth: 'var(--ar-content-max)' } : null
    },
  },
  watch: {
    ready(isReady, wasReady) {
      if (isReady && !wasReady) {
        this.announcement = NEW_COPY.readyToFinalize
        if (!this.pulsedOnce) {
          this.pulsedOnce = true
          this.pulsing = true
          clearTimeout(this.pulseTimer)
          this.pulseTimer = setTimeout(() => {
            this.pulsing = false
          }, PULSE_MS)
        }
      } else if (!isReady) {
        this.announcement = ''
      }
    },
    savedAt() {
      // Mesmo horário (dois autosaves no mesmo minuto) ainda pisca a linha.
      this.savedTick += 1
    },
    // A trava saiu: o anúncio dela não vale mais.
    lockedText(text, previous) {
      if (!text && previous && this.announcement === previous) this.announcement = ''
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onWindowKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onWindowKeydown)
    clearTimeout(this.pulseTimer)
  },
  methods: {
    focusCta(options) {
      if (this.$refs.cta) this.$refs.cta.focus(options)
    },
    onStatusClick() {
      if (this.locked) {
        this.$emit('locked')
        return
      }
      if (this.ready) {
        this.focusCta()
        return
      }
      this.$emit('jump-to-missing')
    },
    onCtaClick() {
      if (this.busy) return
      if (this.locked) {
        // Limpa antes: o mesmo texto anunciado de novo não seria lido.
        this.announcement = ''
        this.$nextTick(() => {
          this.announcement = this.lockedText
        })
        this.$emit('locked')
        return
      }
      if (!this.ready) {
        this.$emit('jump-to-missing')
        return
      }
      this.$emit('finalize')
    },
    // O atalho vale só quando o foco está no formulário desta barra (ancestral `.appointment-report` mais
    // próximo: a página ou o painel da central) ou em lugar nenhum — nunca com um modal aberto.
    shortcutScope() {
      const parent = this.$el && this.$el.parentElement
      return (parent && parent.closest('.appointment-report')) || this.$el
    },
    onWindowKeydown(event) {
      if (!this.shortcut || event.defaultPrevented || event.key !== 'Enter') return
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return
      if (document.body.classList.contains('modal-open')) return
      const active = document.activeElement
      const scope = this.shortcutScope()
      if (active && active !== document.body && scope && !scope.contains(active)) return
      event.preventDefault()
      this.onCtaClick()
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/_appointment-report.scss"></style>

<style lang="scss" scoped>
.ar-action-bar {
  display: flex;
  flex-direction: column;
}

.ar-action-bar--sticky {
  position: sticky;
  bottom: 0;
  z-index: 20;
}

// ---- Banner de erro (acima da barra) ----
.ar-action-bar__error-wrap {
  padding: 0 var(--ar-bar-gutter) 12px;
}

.ar-action-bar__error {
  margin: 0 auto;
  box-shadow: var(--ar-shadow-banner);
}

.ar-action-bar__error-text {
  margin: 0;
}

// ---- Barra ----
.ar-action-bar__bar {
  padding: 0 var(--ar-bar-gutter);
}

// Rodapé do painel da central: 24px (board v4-pendencias).
.ar-action-bar--pane {
  --ar-bar-gutter: 24px;
}

.ar-action-bar--page .ar-action-bar__bar {
  background: var(--ar-bg-bar);
  backdrop-filter: blur(12px);
  border-top: 1px solid var(--ar-hairline);
  box-shadow: var(--ar-shadow-bar);
}

.ar-action-bar--pane .ar-action-bar__bar {
  background: var(--ar-bg-pane-footer);
  border-top: 1px solid var(--ar-hairline);
}

.ar-action-bar__inner {
  min-height: var(--ar-bar-height);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.ar-action-bar--pane .ar-action-bar__inner {
  min-height: 76px;
  padding: 12px 0;
  gap: 16px;
}

// ---- Status + autosave ----
.ar-action-bar__status {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ar-action-bar__status-btn {
  align-self: flex-start;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ar-text-1);
  font-variant-numeric: tabular-nums;
  cursor: pointer;

  &:not(.ar-action-bar__status-btn--ready):hover .ar-action-bar__status-text {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
}

.ar-action-bar__status-btn--ready {
  cursor: default;
}

.ar-action-bar__status-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.ar-action-bar__status-icon {
  flex-shrink: 0;
  color: var(--ar-warning);
}

.ar-action-bar__status-btn--ready .ar-action-bar__status-icon {
  color: var(--ar-success-text);
}

.ar-action-bar__saved {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ar-text-muted);
  animation: ar-soft-in 600ms var(--ar-ease-standard) both;
}

// ---- Ações ----
.ar-action-bar__actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.ar-action-bar__cta {
  min-width: 232px;
  gap: 10px;
  transition:
    background-color 200ms var(--ar-ease-standard),
    border-color 200ms var(--ar-ease-standard),
    color 200ms var(--ar-ease-standard),
    transform var(--ar-dur-press) var(--ar-ease-standard);
}

.ar-action-bar--pane .ar-action-bar__cta {
  min-width: 200px;
}

// Finalizando: continua cheio, sem hover/press.
.ar-action-bar__cta--busy,
.ar-action-bar__cta--busy:hover,
.ar-action-bar__cta--busy:active {
  background: var(--ar-brand);
  border-color: var(--ar-brand);
  color: var(--ar-brand-ink);
  transform: none;
  cursor: progress;
}

@media (max-width: 575.98px) {
  .ar-action-bar__bar {
    padding: 0 16px;
  }

  .ar-action-bar__error-wrap {
    padding: 0 16px 12px;
  }

  .ar-action-bar__inner,
  .ar-action-bar--pane .ar-action-bar__inner {
    padding: 12px 0;
    flex-wrap: wrap;
    gap: 12px;
  }

  .ar-action-bar__actions {
    width: 100%;
  }

  .ar-action-bar__cta,
  .ar-action-bar--pane .ar-action-bar__cta {
    flex: 1 1 auto;
    min-width: 0;
    height: 48px;
  }
}
</style>
